<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"
import type { Services } from "@services"
import { ServiceError } from "@core/exceptions"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Changer le mot de passe",
            subtitle: "Choisissez un nouveau mot de passe",
            password: "Mot de passe",
            passwordPrompt: "Choisir un mot de passe",
            confirmPassword: "Confirmer le mot de passe",
            submit: "Valider",
            success: "Mot de passe mis à jour. Redirection…",
            signOut: "Se déconnecter",
            weak: "Faible",
            medium: "Moyen",
            strong: "Fort",
            ruleMinLength: "Minimum 8 caractères",
            ruleLowercase: "Au moins une minuscule",
            ruleUppercase: "Au moins une majuscule",
            ruleNumeric: "Au moins un chiffre",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { $updateUserPassword } = inject<Services>("services")!

// ================================
// STATE
// ================================
/** Saisie mot de passe et confirmation */
const form = ref({ password: "", confirmPassword: "" })
/** Message d'erreur affiché */
const errorMessage = ref("")
/** Message de succès après mise à jour */
const successMessage = ref("")
/** Afficher le bouton « Se déconnecter » (session expirée) */
const needSignOut = ref(false)

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Vérifie que le mot de passe respecte les règles (longueur, majuscule, minuscule, chiffre).
 * @param p - Mot de passe à valider
 * @returns true si le mot de passe est valide
 */
function validatePassword(p: string): boolean {
    return Boolean(
        p && p.length >= 8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p)
    )
}

// ================================
// COMPUTED
// ================================
/** Formulaire valide (mot de passe conforme et confirmation identique) */
const getIsValidForm = computed(
    () =>
        validatePassword(form.value.password) &&
        form.value.password === form.value.confirmPassword
)

/** Wrapper d'appel mise à jour du mot de passe */
const updateFetch = useFetchWrapper(
    async () => {
        errorMessage.value = ""
        successMessage.value = ""
        const userId = (route.params._id_user as string) || ""
        const validationCode = (route.query.validationCode as string) || ""
        await $updateUserPassword({
            userId,
            password: form.value.password,
            validationCode,
        })
        successMessage.value = t("success")
        setTimeout(() => router.push("/login"), 1000)
    },
    (err: Error) => {
        const e = err as ServiceError
        errorMessage.value = e.message ?? "Erreur"
        needSignOut.value = e.code === "required_no_auth"
    }
)

// ================================
// INTERACTIONS
// ================================
/** Déconnecte l'utilisateur et masque l'erreur. */
function onClickSignOut(): void {
    authStore.logout()
    needSignOut.value = false
    errorMessage.value = ""
}

/** Soumet le formulaire de changement de mot de passe. */
function onSubmit(): void {
    if (!getIsValidForm.value) return
    updateFetch.fetch()
}
</script>

<template>
    <BaseCard>
        <template #title>
            <FormAuthHeader :title="t('title')" :subtitle="t('subtitle')" />
        </template>
        <template #content>
            <form
                class="flex flex-col gap-6 w-full pt-6"
                @submit.prevent="onSubmit"
            >
                <BaseMessage v-if="errorMessage" severity="error">
                    {{ errorMessage }}
                    <BaseButton
                        v-if="needSignOut"
                        :label="t('signOut')"
                        severity="danger"
                        class="mt-2"
                        @click="onClickSignOut"
                    />
                </BaseMessage>
                <BaseMessage v-if="successMessage" severity="success">{{
                    successMessage
                }}</BaseMessage>
                <div class="flex flex-col gap-4">
                    <BaseInputWrapper
                        input-id="auth-update-password"
                        :label="t('password')"
                    >
                        <BasePassword
                            id="auth-update-password"
                            v-model="form.password"
                            toggle-mask
                            fluid
                            class="w-full"
                            :weak-label="t('weak')"
                            :medium-label="t('medium')"
                            :strong-label="t('strong')"
                            :prompt-label="t('passwordPrompt')"
                        >
                            <template #footer>
                                <BaseDivider />
                                <ul
                                    class="pl-2 my-0 leading-normal text-sm text-surface-600"
                                >
                                    <li>{{ t("ruleMinLength") }}</li>
                                    <li>{{ t("ruleLowercase") }}</li>
                                    <li>{{ t("ruleUppercase") }}</li>
                                    <li>{{ t("ruleNumeric") }}</li>
                                </ul>
                            </template>
                        </BasePassword>
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        input-id="auth-update-password-confirm"
                        :label="t('confirmPassword')"
                    >
                        <BasePassword
                            id="auth-update-password"
                            v-model="form.confirmPassword"
                            toggle-mask
                            fluid
                            :feedback="false"
                            class="w-full"
                        />
                    </BaseInputWrapper>
                </div>
                <BaseButton
                    type="submit"
                    :label="t('submit')"
                    :loading="updateFetch.isFetching.value"
                    :disabled="!getIsValidForm"
                    class="w-full"
                />
            </form>
        </template>
    </BaseCard>
</template>
